import Text from "@/components/forms/text";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from '@hookform/resolvers/zod';
import SmallSpinner from "@/components/shared/spinners/small-spinner";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { KeyRound, Save } from 'lucide-react';
import { AUTH_GRAVITEE_ENABLED } from '@/lib/constants';
import { useUpdatePassword } from '@/hooks/api/use-profile';
import { changeMotDePasseSchema, type ChangeMotDePasseValues } from '@/schemas/change-mot-de-passe';

const ChangeMotDePasse = () => {
    const postUpdatePassword = useUpdatePassword();
    const loading = postUpdatePassword.isPending;
    const navigate = useNavigate();

    /**
     * En MFA le changement de mot de passe ferme la session : le jeton Gravitee
     * courant devient antérieur à la révocation, donc refusé. On repart au login
     * de façon assumée, plutôt que de rejoindre le profil pour s'y faire éjecter
     * par le premier appel API. `gravitee_force_login` évite que la session SSO,
     * elle toujours ouverte, reconnecte sans ressaisir le nouveau mot de passe.
     */
    const afterSuccess = () => {
        if (!AUTH_GRAVITEE_ENABLED) {
            void navigate('/mon-profil');
            return;
        }

        try {
            sessionStorage.setItem('gravitee_force_login', 'true');
        } catch {
            // sessionStorage indisponible : on redirige quand même
        }

        void navigate('/login');
    };

    const { register, handleSubmit, watch, formState: { errors } } = useForm<ChangeMotDePasseValues>({
        defaultValues: {
            'old_password': '',
            'password': '',
            'password_confirmation': ''
        },
        resolver: zodResolver(changeMotDePasseSchema) as unknown as Resolver<ChangeMotDePasseValues>,
    });

    return (
        <div className="space-y-6">
            <div className="space-y-2 sm:space-y-3 mt-4">
                <h1 className="text-xl sm:text-2xl font-bold">Je modifie mon mot de passe</h1>
            </div>

            <form onSubmit={handleSubmit(values => postUpdatePassword.mutate({ form: values, successFunction: afterSuccess }))}>
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-primary" />
                            Modification du mot de passe
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-5">
                        <div className="max-w-md">
                            <Text
                                type={'password'}
                                label={'Ancien mot de passe'}
                                {...register('old_password')}
                                value={watch('old_password')}
                                error={errors.old_password?.message}
                                required
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Text
                                type={'password'}
                                label={'Nouveau mot de passe'}
                                {...register('password')}
                                value={watch('password')}
                                error={errors.password?.message}
                                required
                            />
                            <Text
                                type={'password'}
                                label={'Confirmez votre mot de passe'}
                                {...register('password_confirmation')}
                                value={watch('password_confirmation')}
                                error={errors.password_confirmation?.message}
                                required
                            />
                        </div>

                        <div className="pt-2">
                            {loading ? (
                                <SmallSpinner />
                            ) : (
                                <Button type="submit" className="w-full text-white cursor-pointer whitespace-normal py-6">
                                    <Save className="w-4 h-4 shrink-0" />
                                    Je valide ma modification de mot de passe
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </form>
        </div>
    );
};

export default ChangeMotDePasse;
